import { useCallback, useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import 'jquery-ui/ui/widgets/autocomplete';
import _ from 'lodash';
import {
  CALC_TYPE,
  PRESET_FORMATTING_TRIMMED,
  PRESET_PERCENT_RATIO,
  DECIMAL_FIELDS,
  REMOVE_FORMATTING_FIELDS,
  PERMANENT_DOUBLE_ENTRY_FIELDS,
} from '~/app/constants';
import {
  fieldCharSetValidation,
  isValidCRYFieldFormat,
  checkActionKeys,
  stripSpecialChars,
  fixNegatives,
  checkVerifyAddCharacters,
} from '../components/helpers/fieldCharSetValidationHelper.js';
import { translateSpecialOccuranceRow } from '~/app/Utility/general.js';

// Asset form pattern (e.g. A00001)
const assetFormRegExp = /^[A]{1}[0-9]{5}/;
// Table row pattern (e.g. AA0100 — two alpha + two alnum + two digits)
const tableRegExp = /^[A-Za-z]{2}[A-Za-z0-9`@]{2}/;
// Valid input characters
const validChars = /[a-zA-Z0-9\-_ [\]{}!@#$%^&*()=+\\/|.,?~`'":;<>]/;

/**
 * Manages jQuery-based field event binding for the FormViewer.
 *
 * Owns:
 *   - All event binding / unbinding (bindFormEvents / unbindFormEvents)
 *   - Field input state machine: focusin → keydown → change → blur pipeline
 *   - Instance-level mutable refs: previousEl, valueMap, dblBlindList, maxTableRow,
 *     lastRow, subFrm, choices, stmcode, dirtyFlag, fieldVal, prevFieldVal
 *   - Rendering state driven solely by field events:
 *       showDblBlind, menuButtonsChoices, showConsentForm
 *   - Operational state read by the class after form-load:
 *       interviewCurrentEleMap, maintainAutoExtendFocus,
 *       autoExtendTabValue, tabbedField, prevField
 *
 * Does NOT own:
 *   - activeForm / activeFile / interviewMode (owned by useFormNavigation)
 *   - verifyDirty / bookmarkField (owned by FormViewerWrapper refs)
 *   - Redux-connected props (passed via callbacks / refs)
 *   - performCalcProcess verify flow (delegated via onPerformCalcProcess)
 *
 * @param {object}  params
 * @param {React.MutableRefObject}  params.calcAPIRef     CalcAPI instance ref
 * @param {React.MutableRefObject}  params.currentElRef   Currently focused DOM element
 * @param {React.MutableRefObject}  params.formNavRef
 *   Ref to the latest formNav object from useFormNavigation.
 *   Populate synchronously each render: `formNavRef.current = formNav`.
 *   Gives handlers stale-closure-free access to activeForm, activeFile,
 *   interviewMode, getTaxForm, updateFormStack, etc.
 * @param {string}  params.enableDoubleEntry  From Redux: '1' when double-entry is on
 *
 * --- Callbacks (all optional, stored in refs internally) ---
 * @param {function} params.onPerformCalcProcess   (aForm, xIsAssetForm, id, val, calcType, vfyTab)
 * @param {function} params.onOpenChoiceList       (form, fieldId)
 * @param {function} params.onCheckChoiceListForValue (el) → boolean
 * @param {function} params.onHandleChoiceListItem (listItem, fieldId)
 * @param {function} params.onIsPackageAvailable   (value) → boolean
 * @param {function} params.onShowErrorSnackbar    (msg)
 * @param {function} params.onShowPromptSnackbar   (msg)
 * @param {function} params.onHidePromptSnackbar   ()
 * @param {function} params.onSetBookmarkFldID     (id)
 * @param {function} params.onSetFieldFormLinks       (links)
 * @param {function} params.onSetInitScrollPositionTrue   () — Redux: mark scroll at top
 * @param {function} params.onSetInitScrollPositionFalse  () — Redux: mark scroll not at top
 */
const useFormFieldEvents = ({
  calcAPIRef,
  currentElRef,
  formNavRef,
  enableDoubleEntry,
  onPerformCalcProcess,
  onOpenChoiceList,
  onCheckChoiceListForValue,
  onHandleChoiceListItem,
  onIsPackageAvailable,
  onShowErrorSnackbar,
  onShowPromptSnackbar,
  onHidePromptSnackbar,
  onSetBookmarkFldID,
  onSetFieldFormLinks,
  onSetInitScrollPositionTrue,
  onSetInitScrollPositionFalse,
}) => {
  // ─── Callback refs (stable, updated each render) ──────────────────────────
  const onPerformCalcProcessRef = useRef(onPerformCalcProcess);
  const onOpenChoiceListRef = useRef(onOpenChoiceList);
  const onCheckChoiceListForValueRef = useRef(onCheckChoiceListForValue);
  const onHandleChoiceListItemRef = useRef(onHandleChoiceListItem);
  const onIsPackageAvailableRef = useRef(onIsPackageAvailable);
  const onShowErrorSnackbarRef = useRef(onShowErrorSnackbar);
  const onShowPromptSnackbarRef = useRef(onShowPromptSnackbar);
  const onHidePromptSnackbarRef = useRef(onHidePromptSnackbar);
  const onSetBookmarkFldIDRef = useRef(onSetBookmarkFldID);
  const onSetFieldFormLinksRef = useRef(onSetFieldFormLinks);
  const onSetInitScrollPositionTrueRef = useRef(onSetInitScrollPositionTrue);
  const onSetInitScrollPositionFalseRef = useRef(onSetInitScrollPositionFalse);

  useEffect(() => {
    onPerformCalcProcessRef.current = onPerformCalcProcess;
    onOpenChoiceListRef.current = onOpenChoiceList;
    onCheckChoiceListForValueRef.current = onCheckChoiceListForValue;
    onHandleChoiceListItemRef.current = onHandleChoiceListItem;
    onIsPackageAvailableRef.current = onIsPackageAvailable;
    onShowErrorSnackbarRef.current = onShowErrorSnackbar;
    onShowPromptSnackbarRef.current = onShowPromptSnackbar;
    onHidePromptSnackbarRef.current = onHidePromptSnackbar;
    onSetBookmarkFldIDRef.current = onSetBookmarkFldID;
    onSetFieldFormLinksRef.current = onSetFieldFormLinks;
    onSetInitScrollPositionTrueRef.current = onSetInitScrollPositionTrue;
    onSetInitScrollPositionFalseRef.current = onSetInitScrollPositionFalse;
  });

  // Convenience accessor — avoids `.current` everywhere in handlers
  const calcAPI = () => calcAPIRef.current;

  // ─── Instance-variable refs (previously this.* on the class) ─────────────
  const previousElRef = useRef(null);
  /** @type {React.MutableRefObject<Record<string, {value: string, previousValue: string, nxtFld: string}>>} */
  const valueMapRef = useRef({});
  /** @type {React.MutableRefObject<string[]>} */
  const dblBlindListRef = useRef([]);
  const maxTableRowRef = useRef(0);
  const lastRowRef = useRef(0);
  const subFrmRef = useRef('');
  const choicesRef = useRef(false);
  const stmcodeRef = useRef(false);
  const dirtyFlagRef = useRef(false);
  const fieldValRef = useRef('');
  const prevFieldValRef = useRef('');
  const dblBlindFieldRef = useRef(null); // stores the field that triggered the double entry validation

  // ─── Rendering state ──────────────────────────────────────────────────────
  const [showDblBlind, setShowDblBlindState] = useState(false);
  const showDblBlindRef = useRef(false); // sync read in handlers
  const setShowDblBlind = useCallback(v => {
    showDblBlindRef.current = v;
    setShowDblBlindState(v);
  }, []);

  const [menuButtonsChoices, setMenuButtonsChoices] = useState([]);
  const [showConsentForm, setShowConsentForm] = useState(false);

  // ─── Operational state (read by class after form-load) ───────────────────
  const [interviewCurrentEleMap, setInterviewCurrentEleMapState] = useState(() => new Map());
  const interviewCurrentEleMapRef = useRef(new Map());
  const setInterviewCurrentEleMap = useCallback(mapOrUpdater => {
    const next =
      typeof mapOrUpdater === 'function'
        ? mapOrUpdater(interviewCurrentEleMapRef.current)
        : mapOrUpdater;
    interviewCurrentEleMapRef.current = next;
    setInterviewCurrentEleMapState(next);
  }, []);

  const [maintainAutoExtendFocus, setMaintainAutoExtendFocusState] = useState(false);
  const maintainAutoExtendFocusRef = useRef(false);
  const setMaintainAutoExtendFocus = useCallback(v => {
    maintainAutoExtendFocusRef.current = v;
    setMaintainAutoExtendFocusState(v);
  }, []);

  const [autoExtendTabValue, setAutoExtendTabValue] = useState('');
  const autoExtendTabValueRef = useRef('');
  const [tabbedField, setTabbedField] = useState('');
  const tabbedFieldRef = useRef('');

  // Change-event tracking state
  const [prevField, setPrevField] = useState('');
  const prevFieldRef = useRef('');
  const [prevFocusedFieldVal, setPrevFocusedFieldVal] = useState('');
  const [currFieldBeforeChangeVal, setCurrFieldBeforeChangeVal] = useState('');

  // ─── Pure utilities ───────────────────────────────────────────────────────

  /** Escapes special jQuery selector characters in a field ID. */
  const formatIDForJquery = useCallback(rawID => {
    let cleanID = rawID.replace('@', '\\@');
    cleanID = cleanID.replace('`', '\\`');
    return cleanID;
  }, []);

  /**
   * Checks if the given focused element currently has an edited (dirty) value
   * relative to the valueMap cache.
   */
  const isEditedField = useCallback((focusedEl, fieldID) => {
    const vMap = valueMapRef.current;
    if (!vMap[fieldID]) return false;
    const currValue = focusedEl.value;
    const currCacheValue = vMap[fieldID].value;
    let isEdit = currValue !== currCacheValue;
    // Blank date/zip/phone # fields look "edited" but are just pre-formatted
    if (
      currCacheValue === '   -   -' ||
      currCacheValue === '  /  /' ||
      currCacheValue === '     -    -'
    ) {
      isEdit = false;
    }
    return isEdit;
  }, []);

  /**
   * Updates the in-memory field value cache (valueMap).
   * Called from PopulateForm / UpdateForm.
   */
  const updateFieldValueCache = useCallback((field, value, nextField) => {
    const vMap = valueMapRef.current;
    if (vMap[field] === undefined) {
      valueMapRef.current = {
        ...vMap,
        [field]: { previousValue: '', value, nxtFld: nextField },
      };
    } else {
      const pVal = vMap[field].value;
      const pNxtFld = vMap[field].nxtFld;
      if (pVal === value && pNxtFld === nextField) return;
      vMap[field] = { previousValue: pVal, value, nxtFld: nextField };
      valueMapRef.current = vMap;
    }
  }, []);

  /** Adds the 'error' CSS class to a field element. */
  const onError = useCallback(
    field => {
      if (field) {
        const jqueryID = formatIDForJquery(field);
        const errfld = $('#' + jqueryID);
        errfld.addClass('error');
        if (errfld?.[0]?.type === 'checkbox' || errfld?.[0]?.type === 'radio') {
          errfld[0].checked = false;
        }
      }
    },
    [formatIDForJquery],
  );

  // ─── Navigation helpers ───────────────────────────────────────────────────

  /** Fires ReturnCalc for a plain field change. */
  const handleReturnFieldCalc = useCallback(
    (target, val) => {
      const activeForm = formNavRef.current?.activeForm ?? '';
      calcAPI()?.ReturnCalc(
        activeForm,
        assetFormRegExp.test(activeForm) ? 'X' : '',
        target,
        val,
        CALC_TYPE.FIELD_CALC,
      );
    },
    [formNavRef],
  );

  /**
   * Positions the field context menu button next to the focused field.
   * Called on focusin and also from initAutocomplete.
   */
  const renderButtonChoice = useCallback(fieldID => {
    const formContainer = document.getElementsByClassName('formContainer')[0];
    if (!formContainer) return;
    const containerProps = formContainer.getBoundingClientRect();
    containerProps.scrollTop = formContainer.scrollTop;

    const messageButton = document.getElementById('buttonMenuForReturns');
    let field = null;
    if (typeof fieldID !== 'string') {
      const focusedFldID = messageButton?.getAttribute('focusedField');
      if (!focusedFldID) return;
      field = document.getElementById(focusedFldID);
    } else {
      field = document.getElementById(fieldID);
    }
    if (!field || !messageButton) return;

    const scrollLeft = formContainer.scrollLeft ?? 0;
    const coords = field.getBoundingClientRect();
    const left = coords.left - containerProps.x + coords.width + scrollLeft + 'px';
    const top = coords.top + containerProps.scrollTop - containerProps.y + 'px';

    messageButton.style.left = left;
    messageButton.style.top = top;
    messageButton.style.height = coords.height + 'px';
    messageButton.style.setProperty('display', 'flex');
  }, []);

  /**
   * onScroll handler for the form container.
   * Dispatches Redux actions to mark whether the form is scrolled to the top.
   */
  const scrollPositionInit = useCallback(() => {
    const formContainer = document.getElementsByClassName('formContainer')[0];
    if (!formContainer) return;
    if (formContainer.scrollTop === 0) {
      onSetInitScrollPositionTrueRef.current?.();
    } else {
      onSetInitScrollPositionFalseRef.current?.();
    }
  }, []);

  /**
   * Handles tab / enter / arrow focus progression.
   * If the field has been edited, triggers a blur so calc fires first.
   * Otherwise moves directly to the next/previous field.
   */
  const handleNextFld = useCallback(
    el => {
      const vMap = valueMapRef.current;
      const nextField = vMap[el.id]?.nxtFld ?? '';
      const jqueryID = formatIDForJquery(nextField);
      if (nextField.length > 0) {
        $('#' + jqueryID).focus();
      } else {
        switchFocusToNext(el); // defined below; safe because all are in the same scope
      }
    },
    [formatIDForJquery],
  );

  const switchFocusToNext = useCallback(
    el => {
      const activeForm = formNavRef.current?.activeForm ?? '';
      const nextInputs = $(el).parent().nextUntil('.form-cellitem').children('.form_field');
      let nextInput = nextInputs[0];
      if (activeForm === 'BC0601' && el.id === 'BD0300' && nextInputs[1]?.id === 'BA0400') {
        nextInput = nextInputs[1];
      }
      if (nextInput !== undefined) {
        let counter = 1;
        while (nextInput?.classList.contains('hide') && counter !== nextInputs.length) {
          nextInput = nextInputs[counter];
          counter++;
        }
        if (nextInput !== undefined) {
          nextInput.focus();
        }
      }
    },
    [formNavRef],
  );

  const switchFocusToPrev = useCallback(
    el => {
      const activeForm = formNavRef.current?.activeForm ?? '';
      const prevInputs = $(el).parent().prevUntil('.form-cellitem').children('.form_field');
      let prevInput = prevInputs[1];
      if (activeForm === 'BC0601' && el.id === 'BA0400' && prevInputs[1]?.id === 'BD0300') {
        prevInput = prevInputs[1];
      }
      let i = 1;
      if (prevInput !== undefined) {
        while (prevInput.classList.contains('hide')) {
          prevInput = prevInputs[i];
          i++;
        }
        prevInput.focus();
      }
    },
    [formNavRef],
  );

  const handleFocusBehavior = useCallback(
    (fieldID, focusedEl) => {
      const vMap = valueMapRef.current;
      if (vMap[fieldID]) {
        if (isEditedField(focusedEl, fieldID)) {
          $(focusedEl).blur();
        }
        if (vMap[fieldID].nxtFld.length > 0) {
          handleNextFld(currentElRef.current);
        } else {
          switchFocusToNext(currentElRef.current);
        }
      }
    },
    [currentElRef, handleNextFld, isEditedField, switchFocusToNext],
  );

  // ─── Double-blind helpers ─────────────────────────────────────────────────

  const isFieldDblBlind = useCallback(
    e => {
      if (enableDoubleEntry !== '1') return false;
      const id = e.target?.id ?? e.id;
      return id ? dblBlindListRef.current.includes(id.slice(0, 4)) : false;
    },
    [enableDoubleEntry],
  );

  const handleDblindOk = useCallback(
    e => {
      if (!showDblBlindRef.current) return;

      // clicking Cancel causes the double-entry field to blur.
      // skip validation in that case and let the cancel handler run.
      const nextTargetId = e?.relatedTarget?.id;
      if (nextTargetId === 'dblindCancelButton' || nextTargetId === 'dblindOkButton') {
        return;
      }
      const cField = document.getElementById(dblBlindFieldRef.current?.id);
      const dbTextField = document.getElementById('dblindTextField');
      if (!cField || !dbTextField) {
        onShowErrorSnackbarRef.current?.('Failed to find a double entry field');
        setShowDblBlind(false);
        return;
      }
      const dbTextInput = dbTextField.value.replace(/[-/,]|\s/g, '').trim();
      if (!dbTextInput) return;
      if (dbTextInput !== cField.value.replace(/[-/,]|\s/g, '').trim()) {
        onShowErrorSnackbarRef.current?.('Double entry mismatched');
        dbTextField.focus();
        return;
      }
      setShowDblBlind(false);
      handleReturnFieldCalc(dblBlindFieldRef.current?.id ?? '', cField?.value);
    },
    [handleReturnFieldCalc, setShowDblBlind],
  );

  const handleDblindCancel = useCallback(() => {
    const cField = document.getElementById(dblBlindFieldRef.current?.id);
    if (!cField) {
      onShowErrorSnackbarRef.current?.('Double blind entry field not found.');
      setShowDblBlind(false);
      return;
    }

    cField.value = dblBlindFieldRef.current?.previousValue ?? ''; // if cancelling, restore previous value
    setShowDblBlind(false);
    cField.focus();
  }, [setShowDblBlind]);

  // ─── Autocomplete ─────────────────────────────────────────────────────────

  const getChoicelistRow = useCallback((choiceListData, selectedIndex, field) => {
    const selectedRow = choiceListData[selectedIndex];
    if (typeof selectedRow !== 'undefined') {
      onHandleChoiceListItemRef.current?.(selectedRow.ListItem, field);
    }
  }, []);

  const initAutocomplete = useCallback(
    fldData => {
      const choices = fldData.Data;
      const suggestions = choices.map(data => data.ListItem[0].COL);
      const jqueryID = formatIDForJquery(fldData.InitialField.value);
      $('#' + jqueryID)
        .autocomplete({
          source: suggestions,
          change: (event, _ui) => {
            const vMap = valueMapRef.current;
            if (event.target.value !== vMap[event.target.id]?.value) {
              const selectedIndex = suggestions.indexOf(event.target.value);
              getChoicelistRow(choices, selectedIndex, event.target.id);
            }
          },
        })
        .autocomplete('search');
    },
    [formatIDForJquery, getChoicelistRow],
  );

  // ─── jQuery event binding sub-handlers ───────────────────────────────────

  const handleFormButtonEleEvent = useCallback(() => {
    $('.form-button-element')
      .off('click')
      .on('click', e => {
        e.preventDefault();
        const formNav = formNavRef.current;
        formNav?.updateFormStack();

        // CLO: "Copy GUID" button on Info/Status page changed for Season 2025
        if (e.currentTarget.id === '000a739' && /* season */ formNavRef.current?.season < 2025) {
          navigator.clipboard.writeText(valueMapRef.current?.GUID00?.value ?? '').then(
            () =>
              onShowPromptSnackbarRef.current?.(
                (valueMapRef.current?.GUID00?.value ?? '') + ' copied to clipboard',
              ),
            () => onShowErrorSnackbarRef.current?.('Error copying to clipboard'),
          );
        } else {
          calcAPI()?.ReturnButtonPress(
            e.currentTarget.id,
            formNav?.activeForm ?? '',
            e.currentTarget.getAttribute('data-form-name') ?? undefined, // dataFormName: used server-side to verify the button signature
            e.currentTarget.getAttribute('data-cmd') ?? undefined,
            e.currentTarget.getAttribute('data-sig') ?? undefined,
            e.currentTarget.getAttribute('data-bin') ?? undefined,
            e.currentTarget.getAttribute('data-bin-sig') ?? undefined,
          );
        }
      });
  }, [formNavRef]);

  const handleFormButtonIconEvent = useCallback(() => {
    $('.form-button-icon')
      .off('click')
      .on('click', e => {
        e.preventDefault();
        const activeForm = formNavRef.current?.activeForm ?? '';
        const containsActive = e.currentTarget.classList.contains('form-button-icon-active');
        const svgid = e.currentTarget.getAttribute('svgid');
        if (containsActive) {
          $(e.currentTarget).val('');
          e.currentTarget.classList.remove('form-button-icon-active');
          document.querySelector('#' + svgid + ' g')?.setAttribute('fill', '#FEFEFE');
        } else {
          $(e.currentTarget).val('X');
          e.currentTarget.classList.add('form-button-icon-active');
          document.querySelector('#' + svgid + ' g')?.setAttribute('fill', '#d7ecf7');
        }
        calcAPI()?.ReturnCalc(
          activeForm,
          assetFormRegExp.test(activeForm) ? 'X' : '',
          e.currentTarget.id,
          $(e.currentTarget).val(),
          CALC_TYPE.FIELD_CALC,
        );
      });
  }, [formNavRef]);

  const handleFieldLookupEvent = useCallback(() => {
    $('.field_lookup')
      .keydown(e => {
        if (e.target.classList.contains('read-only') || e.target.classList.contains('restricted')) {
          return;
        }
        if (e.altKey && e.which === 67) {
          // alt+c
          onOpenChoiceListRef.current?.(formNavRef.current?.activeForm ?? '', e.target.id);
        }
      })
      .change('focus', e => {
        if (e.target.classList.contains('exclusive')) {
          const isMissingFromList = onCheckChoiceListForValueRef.current?.(e.target);
          if (isMissingFromList) {
            e.target.value = '';
          }
        }
      })
      .keyup(
        _.debounce(e => {
          const activeForm = formNavRef.current?.activeForm ?? '';
          if (
            !(
              e.target.classList.contains('read-only') || e.target.classList.contains('restricted')
            ) &&
            e.target.value?.length > 2
          ) {
            calcAPI()?.ReturnChoiceList(activeForm, e.target.id, e.target.value);
          }
        }, 500),
      );
  }, [formNavRef]);

  const handleWorksheetEvent = useCallback(() => {
    $('.field_wks').keyup(e => {
      if (e.altKey && e.which === 87) {
        // alt+w
        const formNav = formNavRef.current;
        const form = formNav.activeForm + e.target.getAttribute('stmcode');
        formNav?.getTaxForm(form, 'wks', '', currentElRef.current?.id ?? '');
      }
    });
  }, [currentElRef, formNavRef]);

  const handleStatementEvent = useCallback(() => {
    $('.field_stm').keyup(e => {
      if (e.altKey && e.which === 87) {
        const formNav = formNavRef.current;
        const form = formNav.activeForm + e.target.getAttribute('stmcode');
        formNav?.getTaxForm(form, 'stm', '', currentElRef.current?.id ?? '');
      }
    });
  }, [currentElRef, formNavRef]);

  const handleFieldOverflowEvent = useCallback(() => {
    $('.field_ovl').keyup(e => {
      if (e.altKey && e.which === 79) {
        // alt+o
        const formNav = formNavRef.current;
        const activeForm = formNav?.activeForm ?? '';
        const hasStm = e.target.classList.contains('field_stm');
        const hasWks = e.target.classList.contains('field_wks');
        const fromField = currentElRef.current?.id ?? '';
        if (hasStm) {
          formNav?.getTaxForm(activeForm + e.target.getAttribute('stmcode'), 'stm', '', fromField);
        } else if (hasWks) {
          formNav?.getTaxForm(activeForm + e.target.getAttribute('stmcode'), 'wks', '', fromField);
        } else {
          formNav?.getTaxForm(activeForm, 'ovl', '', fromField);
        }
      }
    });
  }, [currentElRef, formNavRef]);

  const handleCheckboxDoubleClickEvent = useCallback(() => {
    $('.form-checkbox-input').dblclick(e => {
      e.preventDefault();
      const activeForm = formNavRef.current?.activeForm ?? '';
      const value = e.target.value === '' ? 'X' : '';
      onPerformCalcProcessRef.current?.(
        activeForm,
        assetFormRegExp.test(activeForm) ? 'X' : '',
        e.target.id,
        value,
        CALC_TYPE.FIELD_CALC,
        1,
      );
    });
  }, [formNavRef]);

  const handleFormFieldChangeEvent = useCallback(
    formField => {
      formField.change(e => {
        const jqueryID = formatIDForJquery(e.target.id);
        const id = e.target.id;
        if (id !== prevFieldRef.current) {
          prevFieldRef.current = id;
          setPrevField(id);
          setPrevFocusedFieldVal($('#' + jqueryID).data('val'));
          setCurrFieldBeforeChangeVal($('#' + jqueryID).val());
        }
      });
    },
    [formatIDForJquery],
  );

  const handleFormFieldFocusInEvent = useCallback(
    formField => {
      formField.focusin(e => {
        if (previousElRef.current === e.target) return;

        const el = document.getElementById('buttonMenuForReturns');
        if (el) {
          el.style.setProperty('display', 'flex');
          el.setAttribute('focusedField', e.target.id);
        }

        prevFieldValRef.current = e.target.value;
        fieldValRef.current = e.target.value;

        const ctl = document.getElementById(e.target.id);
        if (ctl) ctl.value = ctl.value.trim();
        dirtyFlagRef.current = true;

        const formNav = formNavRef.current;
        const activeForm = formNav?.activeForm ?? '';

        // Build context-menu choices for this field
        const fieldButtonMenuChoices = [
          {
            name: 'Clear Override',
            func: () =>
              onPerformCalcProcessRef.current?.(
                activeForm,
                assetFormRegExp.test(activeForm) ? 'X' : '',
                e.target.id,
                e.target.value,
                CALC_TYPE.OVERRIDE_CLEAR,
                1,
              ),
          },
          {
            name: 'Bookmark Field',
            func: () => {
              calcAPI()?.ReturnBookmarkQuery(activeForm, e.target.id);
              onSetBookmarkFldIDRef.current?.(e.target.id);
            },
          },
        ];

        // Display field prompt (sibling span)
        const spanEl = $(e.target).siblings('span');
        spanEl.addClass('transition');
        spanEl.parent('div').css('z-index', '6');

        stmcodeRef.current = $(e.target).attr('stmcode') !== undefined;

        // Update shared currentEl ref
        currentElRef.current = e.target;

        if (e.target.getAttribute('stmcode')) {
          const form = activeForm + e.target.getAttribute('stmcode');
          const wks = e.target.classList?.contains('field_wks');
          const manualEnableWksButton = e.target.classList?.contains('manual_enable_wks_btn');
          const stm = e.target.classList.contains('field_stm') ? 'stm' : 'wks';
          fieldButtonMenuChoices.push({
            name: 'Worksheet',
            func: () => formNav?.getTaxForm(form, stm, '', currentElRef.current?.id ?? ''),
          });
          if ((form && activeForm.length < 7) || wks || manualEnableWksButton) {
            onShowPromptSnackbarRef.current?.('Press alt+w for worksheet');
          }
        } else {
          onHidePromptSnackbarRef.current?.();
        }

        if (
          e.target.getAttribute('choices') &&
          !(e.target.classList.contains('read-only') || e.target.classList.contains('restricted'))
        ) {
          fieldButtonMenuChoices.push({
            name: 'Choices',
            func: () => onOpenChoiceListRef.current?.(activeForm, e.target.id),
          });
          onShowPromptSnackbarRef.current?.('Press alt+c for choices');
          choicesRef.current = true;
        } else {
          onHidePromptSnackbarRef.current?.();
          choicesRef.current = false;
        }

        setMenuButtonsChoices(fieldButtonMenuChoices);

        if (!e.target.getAttribute('linkage')) {
          onSetFieldFormLinksRef.current?.({});
        }

        const jqueryID = formatIDForJquery(e.target.id);
        $('#' + jqueryID).data('val', $('#' + jqueryID).val());

        renderButtonChoice(e.target.id);

        if (formNav?.interviewMode) {
          setInterviewCurrentEleMap(prev => new Map(prev).set(activeForm, e.target));
        }
      });
    },
    [currentElRef, formNavRef, formatIDForJquery, renderButtonChoice, setInterviewCurrentEleMap],
  );

  const handleFormFieldFocusOutEvent = useCallback(
    formField => {
      formField.on('blur', e => {
        const vMap = valueMapRef.current;
        // Skip if same element and value unchanged (except consent fields)
        if (
          previousElRef.current === e.target &&
          vMap &&
          vMap[e.target.id]?.value === e.target.value &&
          !(e.target.id === 'TEXT00' || e.target.id === 'STXT00')
        ) {
          return;
        }

        previousElRef.current = e.target;
        const jqueryID = formatIDForJquery(e.target.id);

        $('#' + jqueryID).removeClass('error');
        const spanEl = $(e.target).siblings('span');
        spanEl.removeClass('transition');
        spanEl.parent('div').css('z-index', '1');

        // Restore un-normalized value if field wasn't actually modified
        if (
          dirtyFlagRef.current &&
          (e.target.value.trim() === fieldValRef.current.trim() ||
            (!e.target.value && !fieldValRef.current.trim()) ||
            (PRESET_FORMATTING_TRIMMED.indexOf(fieldValRef.current.trim()) > -1 &&
              e.target.value.length === 0))
        ) {
          e.target.value = fieldValRef.current;
          dirtyFlagRef.current = false;
        }

        const dataFieldType = e.target.getAttribute('data-field-type');
        const fieldCharSet = e.target.getAttribute('data-field-charset');
        const activeForm = formNavRef.current?.activeForm ?? '';

        if (
          dirtyFlagRef.current &&
          !isValidCRYFieldFormat(
            dataFieldType,
            e.target.value,
            e.target.id,
            fieldCharSet,
            activeForm,
          ) &&
          !e.target.getAttribute('linkage')
        ) {
          let errorMsg = 'Invalid Data Value/Format.';
          const fieldLength = e.target.getAttribute('maxlength');
          switch (dataFieldType) {
            case 'D':
              if (parseInt(fieldLength) === 4) errorMsg += ' Use format MMYY.';
              if (parseInt(fieldLength) === 6) errorMsg += ' Use format MMDDYY.';
              if (parseInt(fieldLength) === 8) errorMsg += ' Use format MMDDYYYY.';
              break;
            case 'E':
            case 'S':
            case 'T':
              errorMsg += ' Expected NNNNNNNNN.';
              break;
            case 'Y':
              if (parseInt(fieldLength) === 4) errorMsg += ' Use format YYYY.';
              if (parseInt(fieldLength) === 6) errorMsg += ' Use format MMYYYY.';
              if (parseInt(fieldLength) === 8) errorMsg += ' Use format MMDDYYYY.';
              break;
            default:
              break;
          }
          onShowErrorSnackbarRef.current?.(errorMsg);
          e.target.value = fieldValRef.current;
          dirtyFlagRef.current = false;
          fieldValRef.current = '';
          return;
        }

        // Consent form: entering 'X' in the text consent field
        if (
          (e.target.id === 'TEXT00' || e.target.id === 'STXT00') &&
          e.target.value?.toUpperCase() === 'X'
        ) {
          setShowConsentForm(true);
        }

        const confirmedDblEntry =
          (enableDoubleEntry && isFieldDblBlind(e)) ||
          PERMANENT_DOUBLE_ENTRY_FIELDS.includes(e.target.id);

        if (
          confirmedDblEntry &&
          (dirtyFlagRef.current || prevFieldValRef.current !== e.target.value) &&
          e.target.value &&
          (fieldValRef.current.replace(/[-/,]|\s/g, '').trim() !==
            e.target.value.replace(/[-/,]|\s/g, '').trim() ||
            fieldValRef.current.trim() !== e.target.value.trim()) &&
          !showDblBlindRef.current
        ) {
          // Snapshot the field that triggered double entry validation (and its prior value).
          // Used to restore on cancel and to run validation/calcs against the correct field even if focus moves.
          dblBlindFieldRef.current = {
            id: e.target.id,
            previousValue: valueMapRef.current[e.target.id]?.value ?? '', // store original value before double verify in case they cancel
          };

          setShowDblBlind(true);
        }

        dirtyFlagRef.current = false;
        fieldValRef.current = '';

        // State Interview Mode — reset invalid state package selection
        if (
          e.target.value &&
          (e.target.title === 'Non-Resident State Package' ||
            e.target.title === 'Resident State Package') &&
          !onIsPackageAvailableRef.current?.(e.target.value.toUpperCase())
        ) {
          handleReturnFieldCalc(currentElRef.current?.id ?? '', 'XX');
          stmcodeRef.current = false;
          choicesRef.current = false;
          onHidePromptSnackbarRef.current?.();
          return;
        }

        // Trigger field calc if value changed and not awaiting double-blind
        if (vMap && vMap[e.target.id]?.value !== e.target.value && !showDblBlindRef.current) {
          handleReturnFieldCalc(currentElRef.current?.id ?? '', currentElRef.current?.value ?? '');

          // Auto-extend: reload if user edited the last table row
          let currentRow = 0;
          if (tableRegExp.test(e.target.id)) {
            currentRow = translateSpecialOccuranceRow(e.target.id.substring(2, 4));
          }
          if (currentRow === maxTableRowRef.current && lastRowRef.current > 0) {
            const form = activeForm;
            setMaintainAutoExtendFocus(true);
            const formNav = formNavRef.current;
            if (subFrmRef.current !== '' && subFrmRef.current !== 'ovl') {
              formNav?.getTaxForm(
                form,
                'stm',
                formNav?.activeFile ?? '',
                currentElRef.current?.id ?? '',
                false,
              );
            } else {
              formNav?.getTaxForm(form, '', formNav?.activeFile ?? '', '');
            }
          }
        }

        stmcodeRef.current = false;
        choicesRef.current = false;
        onHidePromptSnackbarRef.current?.();
      });
    },
    [
      currentElRef,
      enableDoubleEntry,
      formatIDForJquery,
      formNavRef,
      handleReturnFieldCalc,
      isFieldDblBlind,
      setMaintainAutoExtendFocus,
      setShowDblBlind,
    ],
  );

  const handleFormFieldKeyDownEvent = useCallback(
    formField => {
      formField.keydown(e => {
        const field = e.target.id;
        const vMap = valueMapRef.current;
        switch (e.which) {
          case 32: // spacebar
            if (e.target.classList.contains('form-checkbox-input')) {
              e.preventDefault();
              if (e.target.value === 'X') {
                $(e.target).val('');
              } else {
                $(e.target).val('X');
              }
              switchFocusToNext(e.target);
            }
            break;
          case 9: // tab
            e.preventDefault();
            if (e.shiftKey) {
              switchFocusToPrev(e.target);
            } else {
              handleFocusBehavior(field, e.target);
            }
            break;
          case 13: // enter
            e.preventDefault();
            handleFocusBehavior(field, e.target);
            break;
          case 38: // up arrow
            if (isEditedField(e.target, field) && e.target.classList.contains('field_lookup'))
              break;
            e.preventDefault();
            e.stopImmediatePropagation();
            switchFocusToPrev(e.target);
            break;
          case 40: // down arrow
            if (isEditedField(e.target, field) && e.target.classList.contains('field_lookup'))
              break;
            e.preventDefault();
            e.stopImmediatePropagation();
            handleFocusBehavior(field, e.target);
            break;
          case 37: // left arrow
            if (
              e.target.classList.contains('read-only') ||
              e.target.classList.contains('restricted') ||
              e.target.selectionStart <= 0
            ) {
              e.preventDefault();
              e.stopImmediatePropagation();
              switchFocusToPrev(e.target);
            } else if (PRESET_FORMATTING_TRIMMED.indexOf(e.target.value) > -1) {
              e.target.value = '';
            }
            break;
          case 39: // right arrow
            if (
              e.target.selectionStart >= e.target.value.length ||
              e.target.classList.contains('read-only') ||
              e.target.classList.contains('restricted')
            ) {
              e.preventDefault();
              e.stopImmediatePropagation();
              handleFocusBehavior(field, e.target);
            }
            break;
          case 27: // esc
            if (isEditedField(e.target, field)) {
              e.target.value = vMap[field]?.value ?? '';
            } else {
              e.target.value = vMap[field]?.previousValue ?? '';
            }
            break;
          case 46: // delete
            if (
              !e.target.classList.contains('read-only') &&
              !e.target.classList.contains('restricted')
            ) {
              e.target.value = '';
            }
            break;
          case 189: // minus
            if (e.target.getAttribute('data-field-type') === 'L' && e.target.value.length > 0) {
              stripSpecialChars(e);
            }
            if (e.target.getAttribute('data-field-type') === 'A') {
              // e equals _ so we need to prevent that input for field type A
              fieldCharSetValidation(e);
            }
            break;
          case 191: // forward slash /
          case 111: // numpad forward slash /
            if (checkVerifyAddCharacters(e)) {
              break;
            }
            fieldCharSetValidation(e);
            break;
          case 8: // backspace
            if (
              !e.target.classList.contains('read-only') &&
              !e.target.classList.contains('restricted')
            ) {
              if (PRESET_FORMATTING_TRIMMED.indexOf(e.target.value) > -1) {
                e.target.value = '';
              } else if (e.target.getAttribute('data-field-type')?.toUpperCase() === 'L') {
                if (e.target.value[0] === '(' || e.target.value[0] === '-') {
                  fixNegatives(e);
                } else {
                  stripSpecialChars(e);
                }
              } else if (
                REMOVE_FORMATTING_FIELDS.indexOf(
                  e.target.getAttribute('data-field-type')?.toUpperCase(),
                ) > -1
              ) {
                stripSpecialChars(e);
              }
            }
            break;
          default: {
            const input = e.key;
            if (checkActionKeys(e) && validChars.test(input) && !e.altKey) {
              if (
                DECIMAL_FIELDS.indexOf(e.target.getAttribute('data-field-type')?.toUpperCase()) > -1
              ) {
                const maxLen = parseInt(e.target.getAttribute('maxLength'));
                const len = e.target.value.length;
                if (maxLen === len) {
                  const cur = e.target.selectionStart;
                  const text1 = e.target.value.slice(0, cur);
                  const text2 = e.target.value.slice(cur, len);
                  e.target.value = text1.concat(input, text2);
                }
                if (PRESET_PERCENT_RATIO.indexOf(e.target.value) > -1) {
                  e.target.value = '';
                }
              } else if (e.target.getAttribute('data-field-type')?.toUpperCase() === 'L') {
                if (e.target.value[0] === '(' || e.target.value[0] === '-') {
                  fixNegatives(e);
                } else {
                  stripSpecialChars(e);
                }
              } else if (
                PRESET_FORMATTING_TRIMMED.indexOf(e.target.value) > -1 ||
                e.target.value === '  /  /'
              ) {
                e.target.value = '';
              }
              fieldCharSetValidation(e);
            }

            // Track tab+autoextend value for the form_load auto-extend case
            const nextVal = e.target.value + input;
            autoExtendTabValueRef.current = nextVal;
            tabbedFieldRef.current = e.target.id;
            setAutoExtendTabValue(nextVal);
            setTabbedField(e.target.id);
            break;
          }
        }
      });
    },
    [handleFocusBehavior, isEditedField, switchFocusToNext, switchFocusToPrev],
  );

  // ─── Public API ───────────────────────────────────────────────────────────

  const unbindFormEvents = useCallback(() => {
    $('.form_field').off();
  }, []);

  /**
   * Rebinds all jQuery event handlers for the current form's DOM.
   * Call this after PopulateForm / UpdateForm has injected new HTML.
   */
  const bindFormEvents = useCallback(() => {
    handleFormButtonEleEvent();
    handleFormButtonIconEvent();
    handleFieldLookupEvent();
    handleWorksheetEvent();
    handleStatementEvent();
    handleFieldOverflowEvent();
    handleCheckboxDoubleClickEvent();

    const formField = $('.form_field');
    handleFormFieldChangeEvent(formField);
    handleFormFieldFocusInEvent(formField);
    handleFormFieldFocusOutEvent(formField);
    handleFormFieldKeyDownEvent(formField);
  }, [
    handleFormButtonEleEvent,
    handleFormButtonIconEvent,
    handleFieldLookupEvent,
    handleWorksheetEvent,
    handleStatementEvent,
    handleFieldOverflowEvent,
    handleCheckboxDoubleClickEvent,
    handleFormFieldChangeEvent,
    handleFormFieldFocusInEvent,
    handleFormFieldFocusOutEvent,
    handleFormFieldKeyDownEvent,
  ]);

  return {
    // ── State exposed to FormViewer class for rendering ──────────────────
    showDblBlind,
    setShowDblBlind,
    menuButtonsChoices,
    setMenuButtonsChoices,
    showConsentForm,
    setShowConsentForm,
    interviewCurrentEleMap,
    setInterviewCurrentEleMap,
    maintainAutoExtendFocus,
    setMaintainAutoExtendFocus,
    autoExtendTabValue,
    autoExtendTabValueRef, // sync read in form_load handler
    tabbedField,
    tabbedFieldRef, // sync read in form_load handler
    prevField,
    prevFocusedFieldVal,
    currFieldBeforeChangeVal,

    // ── Mutable refs exposed to FormViewer class ─────────────────────────
    previousElRef,
    valueMapRef,
    dblBlindListRef,
    maxTableRowRef,
    lastRowRef,
    subFrmRef,
    choicesRef,
    stmcodeRef,

    // ── Methods exposed to FormViewer class ──────────────────────────────
    bindFormEvents,
    unbindFormEvents,
    handleReturnFieldCalc,
    handleDblindOk,
    handleDblindCancel,
    isFieldDblBlind,
    formatIDForJquery,
    renderButtonChoice,
    scrollPositionInit,
    updateFieldValueCache,
    onError,
    initAutocomplete,
    switchFocusToNext,
  };
};

export default useFormFieldEvents;
